import React, { useState, useEffect } from 'react';
import axios from 'axios';

const GmailConnection = () => {
  const [gmailStatus, setGmailStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    checkGmailStatus();
    
    // Check for URL parameters indicating connection result
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('gmail_connected') === 'true') {
      const connectedEmail = urlParams.get('email');
      setSuccessMessage(`Gmail account ${connectedEmail ? `(${decodeURIComponent(connectedEmail)})` : ''} connected successfully!`);
      // Remove the parameters and refresh status
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => {
        checkGmailStatus();
        setSuccessMessage('');
      }, 3000);
    } else if (urlParams.get('gmail_error')) {
      const errorMsg = decodeURIComponent(urlParams.get('gmail_error'));
      setError(`Failed to connect Gmail: ${errorMsg}`);
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => setError(''), 5000);
    }
  }, []);

  const checkGmailStatus = async () => {
    try {
      const response = await axios.get('/api/gmail-oauth/status');
      setGmailStatus(response.data);
      setError('');
    } catch (error) {
      console.error('Error checking Gmail status:', error);
      setError('Failed to check Gmail connection status');
    } finally {
      setLoading(false);
    }
  };

  const connectGmail = async () => {
    try {
      setConnecting(true);
      setError('');
      
      const response = await axios.get('/api/gmail-oauth/auth-url');
      
      if (response.data.success) {
        // Open OAuth2 flow in same window
        window.location.href = response.data.authUrl;
      } else {
        throw new Error(response.data.message || 'Failed to get authorization URL');
      }
    } catch (error) {
      console.error('Error connecting Gmail:', error);
      setError(error.response?.data?.message || 'Failed to initiate Gmail connection');
      setConnecting(false);
    }
  };

  const disconnectGmail = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Gmail account? You will no longer be able to send emails through this account.')) {
      return;
    }

    try {
      setError('');
      const response = await axios.post('/api/gmail-oauth/disconnect');
      
      if (response.data.success) {
        setGmailStatus({ success: true, connected: false });
        setSuccessMessage('Gmail account disconnected successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error(response.data.message || 'Failed to disconnect Gmail');
      }
    } catch (error) {
      console.error('Error disconnecting Gmail:', error);
      setError(error.response?.data?.message || 'Failed to disconnect Gmail account');
    }
  };

  const forceDisconnectGmail = async () => {
    if (!window.confirm('⚠️ FORCE DISCONNECT: This will completely remove your Gmail connection and fix any authentication issues. Are you sure?')) {
      return;
    }

    try {
      setError('');
      const response = await axios.post('/api/gmail-oauth/force-disconnect');
      
      if (response.data.success) {
        setGmailStatus({ success: true, connected: false });
        setSuccessMessage('Gmail account completely removed. You can now reconnect with fresh credentials.');
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        throw new Error(response.data.message || 'Failed to force disconnect Gmail');
      }
    } catch (error) {
      console.error('Error force disconnecting Gmail:', error);
      setError(error.response?.data?.message || 'Failed to force disconnect Gmail account');
    }
  };

  const refreshToken = async () => {
    try {
      setError('');
      const response = await axios.post('/api/gmail-oauth/refresh-token');
      
      if (response.data.success) {
        setSuccessMessage('Gmail token refreshed successfully');
        checkGmailStatus(); // Refresh the status
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error(response.data.message || 'Failed to refresh token');
      }
    } catch (error) {
      console.error('Error refreshing Gmail token:', error);
      setError(error.response?.data?.message || 'Failed to refresh Gmail token');
    }
  };

  const isTokenExpired = () => {
    if (!gmailStatus?.tokenExpiry) return false;
    return new Date(gmailStatus.tokenExpiry) <= new Date();
  };

  if (loading) {
    return (
      <div className="gmail-connection-container">
        <div className="loading-spinner">Checking Gmail connection...</div>
      </div>
    );
  }

  return (
    <div className="gmail-connection-container">
      <div className="gmail-connection-section">
        <div className="gmail-header">
          <h3>Gmail Integration for Email Submissions</h3>
          <p className="gmail-description">
            Connect your Gmail account to send lender submissions directly from your email address. 
            This integration is used exclusively for email-based lender submissions and does not affect your app login.
          </p>
        </div>
        
        {/* Success/Error Messages */}
        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}
        
        {gmailStatus?.connected ? (
          <div className="gmail-connected">
            <div className={`connection-status ${isTokenExpired() ? 'warning' : 'success'}`}>
              <div className="status-icon">
                {isTokenExpired() ? '⚠️' : '✅'}
              </div>
              <div className="status-details">
                <div className="status-title">
                  <strong>{isTokenExpired() ? 'Gmail Connected (Token Expired)' : 'Gmail Connected'}</strong>
                </div>
                <div className="gmail-email">{gmailStatus.gmailEmail}</div>
                <div className="connection-info">
                  <span className="connection-date">
                    Connected: {new Date(gmailStatus.connectedAt).toLocaleDateString()}
                  </span>
                  {gmailStatus.tokenExpiry && (
                    <span className="token-expiry">
                      Token expires: {new Date(gmailStatus.tokenExpiry).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="gmail-actions">
              {isTokenExpired() && (
                <button 
                  className="refresh-btn"
                  onClick={refreshToken}
                >
                  Refresh Token
                </button>
              )}
              <button 
                className="disconnect-btn"
                onClick={disconnectGmail}
              >
                Disconnect Gmail
              </button>
              <button 
                className="force-disconnect-btn"
                onClick={forceDisconnectGmail}
                title="Use this if you're getting authentication errors"
              >
                🔧 Fix Auth Issues
              </button>
            </div>
          </div>
        ) : (
          <div className="gmail-disconnected">
            <div className="connection-status disconnected">
              <div className="status-icon">⚠️</div>
              <div className="status-details">
                <div className="status-title">
                  <strong>Gmail Not Connected</strong>
                </div>
                <p>Connect your Gmail account to enable email submissions to lenders</p>
              </div>
            </div>
            
            <div className="gmail-actions">
              <button 
                className="connect-btn"
                onClick={connectGmail}
                disabled={connecting}
              >
                {connecting ? (
                  <>
                    <span className="spinner"></span>
                    Connecting...
                  </>
                ) : (
                  <>
                    <span className="gmail-icon">📧</span>
                    Connect Gmail
                  </>
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* Help Section */}
        <div className="gmail-help">
          <h4>What permissions are needed?</h4>
          <ul>
            <li><strong>Send emails:</strong> Send lender submissions from your Gmail account</li>
            <li><strong>Email address access:</strong> Verify your Gmail address for submissions</li>
          </ul>
          <div className="gmail-security">
            <h4>Security & Privacy</h4>
            <ul>
              <li>We cannot read your existing emails</li>
              <li>We only send emails when you submit to lenders</li>
              <li>You can revoke access at any time</li>
              <li>Your credentials are securely encrypted</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GmailConnection;